import { CircleAlert, type LucideIcon } from "lucide-react";

interface InfoBannerProps {
  /** Visual size variant. "sm" is inline/tooltip scale; "lg" is page-level banner scale. */
  size?: "sm" | "lg";
  /** Lucide icon component. Defaults to CircleAlert. */
  icon?: LucideIcon;
  children: React.ReactNode;
  className?: string;
}

/**
 * @component InfoBanner
 * @description Contextual info callout with an icon and body text. Two size
 * variants matching the design system spec:
 * - **sm** (default) — inline / tooltip scale (`p-3`, icon `w-4 h-4`)
 * - **lg** — page-level banner scale (`p-4`, icon `w-5 h-5`)
 * @param size - "sm" (default) or "lg"
 * @param icon - Lucide icon component. Defaults to CircleAlert.
 * @param children - Message content.
 * @returns A `bg-canvas` banner with leading icon and text.
 */
export default function InfoBanner({
  size = "sm",
  icon: IconComponent = CircleAlert,
  children,
  className = "",
}: InfoBannerProps) {
  const isLg = size === "lg";
  return (
    <div
      className={`bg-canvas text-body-base text-muted-text leading-relaxed flex ${isLg ? "p-4 gap-3" : "p-3 gap-2"} ${className}`}
      role="status"
    >
      <IconComponent
        className={`shrink-0 mt-0.5 text-muted-text ${isLg ? "w-5 h-5" : "w-4 h-4"}`}
        aria-hidden
      />
      {/* TODO: update to add p to info banner children */}
      <div className="break-words min-w-0">{children}</div>
    </div>
  );
}
